import type { MarkerSize } from '@/features/ui-mode/store/ui-mode-store';
import type { PinColor } from '@/features/charge-points/types/charge-point.types';

/**
 * Default status colors — today's hardcoded ring/pin RGBs. When no custom
 * `marker_color_*` param is set, the gradient is byte-for-byte unchanged.
 */
const DEFAULT_STATUS_COLORS = {
  available: 'rgb(34, 197, 94)', // green
  busy: 'rgb(249, 115, 22)', // orange
  error: 'rgb(239, 68, 68)', // red
  disconnected: 'rgb(107, 114, 128)', // grey
} as const;

/**
 * Custom per-status colors sourced from the `marker_color_*` URL params.
 * A null/undefined field means "use the default for that status".
 */
export interface StatusColors {
  available?: string | null;
  busy?: string | null;
  disconnected?: string | null;
  error?: string | null;
}

interface CountColors {
  green?: number;
  grey?: number;
  gray?: number;
  orange?: number;
  red?: number;
  blue?: number;
}

/**
 * Total count across a status-color breakdown (0 when absent/empty).
 */
export function countColorsTotal(colors: CountColors | undefined): number {
  if (!colors) return 0;
  return (
    (colors.green || 0) +
    (colors.grey || 0) +
    (colors.gray || 0) +
    (colors.orange || 0) +
    (colors.red || 0) +
    (colors.blue || 0)
  );
}

/**
 * Build a single-segment breakdown from a marker's dominant `pin.color`. Used
 * as a fallback when the backend doesn't send a per-status breakdown, so the
 * marker still renders its status color (and picks up custom cluster colors)
 * instead of a plain black fallback.
 */
export function colorsFromPinColor(color: PinColor | undefined): CountColors {
  return {
    green: color === 'green' ? 1 : 0,
    orange: color === 'orange' ? 1 : 0,
    red: color === 'red' ? 1 : 0,
    grey: color !== 'green' && color !== 'orange' && color !== 'red' ? 1 : 0,
  };
}

/**
 * Resolve each status to its effective color — the custom `marker_color_*`
 * value when set, otherwise the default. Shared by the marker ring and the
 * hover tooltip so the two always match.
 */
export function resolveStatusColors(statusColors?: StatusColors) {
  return {
    available: statusColors?.available || DEFAULT_STATUS_COLORS.available,
    busy: statusColors?.busy || DEFAULT_STATUS_COLORS.busy,
    error: statusColors?.error || DEFAULT_STATUS_COLORS.error,
    disconnected:
      statusColors?.disconnected || DEFAULT_STATUS_COLORS.disconnected,
  };
}

/**
 * Create a conic gradient for the segmented status ring based on color counts.
 * Note: backend uses 'blue' to represent 'disconnected' status, which we
 * display as grey. `statusColors` (from marker_color_* params) overrides the
 * default RGB stops; absent → identical gradient as before.
 */
export function getConicGradient(
  colors: CountColors | undefined,
  statusColors?: StatusColors
): string {
  if (!colors) return '';

  const green = colors.green || 0;
  const grey = colors.grey || colors.gray || 0;
  const orange = colors.orange || 0;
  const red = colors.red || 0;
  const blue = colors.blue || 0;

  const total = green + grey + orange + red + blue;
  if (total === 0) return '';

  const {
    available: availableColor,
    busy: busyColor,
    error: errorColor,
    disconnected: disconnectedColor,
  } = resolveStatusColors(statusColors);

  // Build gradient stops with hard stops for clean segments
  const stops: string[] = [];
  let currentDeg = 0;

  if (green > 0) {
    const degrees = (green / total) * 360;
    stops.push(`${availableColor} ${currentDeg}deg ${currentDeg + degrees}deg`);
    currentDeg += degrees;
  }
  if (orange > 0) {
    const degrees = (orange / total) * 360;
    stops.push(`${busyColor} ${currentDeg}deg ${currentDeg + degrees}deg`);
    currentDeg += degrees;
  }
  if (red > 0) {
    const degrees = (red / total) * 360;
    stops.push(`${errorColor} ${currentDeg}deg ${currentDeg + degrees}deg`);
    currentDeg += degrees;
  }
  // Combine gray and blue counts - both represent disconnected/passive
  const totalGray = grey + blue;
  if (totalGray > 0) {
    const degrees = (totalGray / total) * 360;
    stops.push(
      `${disconnectedColor} ${currentDeg}deg ${currentDeg + degrees}deg`
    );
  }

  return `conic-gradient(from 0deg, ${stops.join(', ')})`;
}

/**
 * Map a single pin color to its custom override color (from marker_color_*
 * params), or null when no override is set for that status.
 */
export function getCustomBorderColor(
  color: PinColor,
  statusColors: StatusColors
): string | null {
  switch (color) {
    case 'green':
      return statusColors.available || null;
    case 'orange':
      return statusColors.busy || null;
    case 'red':
      return statusColors.error || null;
    case 'gray':
    default:
      return statusColors.disconnected || null;
  }
}

const PILL_SIZES: Record<MarkerSize, string> = {
  xs: 'h-6 px-1.5 gap-1 text-[11px]',
  s: 'h-7 px-2 gap-1 text-xs',
  m: 'h-8 px-2.5 gap-1.5 text-[13px]', // default
};

/**
 * Size classes for the single charge-point pill, honoring `marker_size`.
 */
export function getPillSizeClass(markerSize: MarkerSize | null): string {
  return PILL_SIZES[markerSize ?? 'm'];
}
